import {
	useQuery,
	useMutation,
	useQueryClient,
	UseQueryOptions
} from "@tanstack/react-query";
import { useState, useEffect } from "react";
import {
	searchUsers,
	createUser,
	getUser,
	getCurrentUser,
	createUserAuth,
	getUsersByRole,
	getUsersByCompany,
	getUserSettings,
	patchUserSettings
} from "../api/users-api";
import { User } from "../models/User";
import { UserSettings } from "../models/UserSettings";
import { CreateInternalUserRequest } from "../types/auth";
import JSONPatchOperation from "../utils/JSONPatchOperation";
import { useSnackbarContext } from "../contexts/useSnackBarContext";
import { useLinearProgressContext } from "../contexts/useLinearProgressContext";
import JSONPatchOperationType from "../utils/JSONPatchOperationType";
import { LanguageCode } from "../models/enums/LanguageCode";

/**
 * Hook to get a user by ID
 */
export const useUser = (id: number) => {
	return useQuery<User, Error>({
		queryKey: ["users", id],
		queryFn: () => getUser(id),
		enabled: id !== 0
	});
};

/**
 * Hook to search users by criteria with debouncing
 */
export const useSearchUsers = (criteria: { email?: string; firstName?: string }) => {
	// Add state for debounced value
	const [debouncedCriteria, setDebouncedCriteria] = useState({
		email: "",
		firstName: ""
	});

	// Update debounced value after delay
	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedCriteria({
				email: criteria.email || "",
				firstName: criteria.firstName || ""
			});
		}, 300); // 300ms delay for debounce

		return () => {
			clearTimeout(handler);
		};
	}, [criteria.email, criteria.firstName]);

	return useQuery({
		queryKey: ["users", debouncedCriteria],
		queryFn: () => searchUsers(debouncedCriteria),
		enabled: !!(
			(debouncedCriteria.email && debouncedCriteria.email.length >= 3) ||
			(debouncedCriteria.firstName && debouncedCriteria.firstName.length >= 3)
		), // Only run query if we have at least 3 characters
		staleTime: 10000 // Cache results for 10 seconds
	});
};

/**
 * Hook to create a new user
 */
export const useCreateUser = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (user: User) => createUser(user),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
		}
	});
};

/**
 * Hook to create a new user auth record
 */
export const useCreateUserAuth = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (userData: CreateInternalUserRequest) => createUserAuth(userData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["users"] });
		}
	});
};

/**
 * Hook to get the current user with language support
 */
export const useCurrentUser = (
	isEnabled: boolean = true,
	options?: Omit<UseQueryOptions<User | null, Error>, "queryKey" | "queryFn" | "enabled">
) => {
	return useQuery<User | null, Error>({
		queryKey: ["currentUser"],
		queryFn: () => getCurrentUser(),
		enabled: isEnabled,
		// Merge with default options
		retry: 1,
		staleTime: 10000,
		gcTime: 60000,
		...options
	});
};

/**
 * Hook to get users by role
 */
export const useUsersByRole = (role: string) => {
	return useQuery({
		queryKey: ["users", "role", role],
		queryFn: () => getUsersByRole(role),
		enabled: !!role,
		staleTime: 30000 // Cache for 30 seconds
	});
};

/**
 * Hook to get users by company
 */
export const useUsersByCompany = (companyId?: number) => {
	return useQuery({
		queryKey: ["users", "company", companyId],
		queryFn: () => getUsersByCompany(companyId!),
		enabled: !!companyId,
		staleTime: 30000 // Cache for 30 seconds
	});
};

/**
 * Hook to get user settings with language support
 */
export const useUserSettings = (userId: number, enabled: boolean = true) => {
	return useQuery<UserSettings, Error>({
		queryKey: ["userSettings", userId],
		queryFn: () => getUserSettings(userId),
		enabled: enabled && userId > 0,
		refetchOnWindowFocus: false
	});
};

/**
 * Hook to update user settings using JSON Patch operations
 */
export const usePatchUserSettings = () => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useSnackbarContext();
	const { showProgress, hideProgress } = useLinearProgressContext();

	return useMutation({
		mutationFn: async ({
			userId,
			newLanguage
		}: {
			userId: number;
			newLanguage: LanguageCode;
		}) => {
			showProgress();
			await patchUserSettings(userId, [
				{
					op: JSONPatchOperationType.Replace,
					path: "/preferredLanguage",
					value: newLanguage
				}
			] as JSONPatchOperation[]);
		},
		onSuccess: (_, { userId }) => {
			hideProgress();
			queryClient.invalidateQueries({ queryKey: ["userSettings", userId] });
			queryClient.invalidateQueries({ queryKey: ["currentUser"] });
			// PreferredLanguage anchors the translation UX (source language of every translatable
			// field): re-fetch the cells, flags and targets so the UI re-anchors on the new source.
			// The server converts the new language's AI cells to human on the switch.
			queryClient.invalidateQueries({ queryKey: ["entityTranslations"] });
			queryClient.invalidateQueries({ queryKey: ["entityTranslationFlags"] });
			queryClient.invalidateQueries({ queryKey: ["targetLanguages"] });
			showSuccess("settings.updateSuccess");
		},
		onError: () => {
			hideProgress();
			showError("settings.updateError");
		}
	});
};
